/**
 * iter-170ai — Advanced Exports card.
 *
 * Lets an entitled user download movements as CSV or XLSX with a
 * lightweight filter form (date range, type, currency). The buttons
 * trigger a direct browser download (the backend streams the response
 * with a ``Content-Disposition: attachment`` header) so no clientside
 * blob plumbing is needed.
 *
 * Lives inside ``<LockedSection featureKey="advanced_exports">``.
 */
import { useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Download, FileSpreadsheet, FileText } from "lucide-react";
import { useLang } from "../contexts/LangContext";
import { tx } from "../i18n";
import { api } from "../lib/api";
import { toast } from "sonner";

const TYPE_OPTIONS = [
  { v: "all",      labelEs: "Todos", labelEn: "All",       labelPt: "Todos" },
  { v: "income",   labelEs: "Ingresos", labelEn: "Income", labelPt: "Receitas" },
  { v: "expense",  labelEs: "Gastos", labelEn: "Expenses", labelPt: "Despesas" },
  { v: "transfer", labelEs: "Transferencias", labelEn: "Transfers", labelPt: "Transferências" },
];

export function AdvancedExportsCard() {
  const { lang } = useLang();
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [type, setType] = useState("all");
  const [currency, setCurrency] = useState("");
  const [downloading, setDownloading] = useState(null);

  const params = () => {
    const p = {};
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    if (type && type !== "all") p.type = type;
    if (currency) p.currency = currency.toUpperCase();
    return p;
  };

  const download = async (fmt) => {
    setDownloading(fmt);
    try {
      // Stream via axios so the Authorization header is added by our
      // configured interceptor (a plain ``<a download>`` link would
      // bypass it and hit the 401 path).
      const res = await api.get(`/exports/movements.${fmt}`, {
        params: params(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      // Pull filename from the Content-Disposition header when present.
      const dispo = res.headers["content-disposition"] || "";
      const match = /filename="([^"]+)"/.exec(dispo);
      a.download = match ? match[1] : `lumfi-movements.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(tx(lang, "Download started", "Descarga iniciada", "Download iniciado"));
    } catch {
      toast.error(tx(lang, "Export failed", "Error al exportar", "Erro ao exportar"));
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Card className="p-5 rounded-2xl space-y-4" data-testid="advanced-exports-card">
      <div className="flex items-center gap-2">
        <Download className="h-4 w-4 text-slate-700" />
        <h3 className="font-semibold text-slate-900">
          {tx(lang, "Advanced exports", "Exportaciones avanzadas", "Exportações avançadas")}
        </h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1 block">
            {tx(lang, "From", "Desde", "De")}
          </label>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            data-testid="export-date-from"
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1 block">
            {tx(lang, "To", "Hasta", "Até")}
          </label>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            data-testid="export-date-to"
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1 block">
            {tx(lang, "Type", "Tipo", "Tipo")}
          </label>
          <select /* design-system: ALLOW legacy-todo */
            value={type}
            onChange={(e) => setType(e.target.value)}
            data-testid="export-type"
            className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
          >
            {TYPE_OPTIONS.map((o) => (
              <option key={o.v} value={o.v}>{tx(lang, o.labelEn, o.labelEs, o.labelPt)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1 block">
            {tx(lang, "Currency", "Moneda", "Moeda")}
          </label>
          <Input
            placeholder={tx(lang, "All", "Todas", "Todas")}
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            maxLength={3}
            data-testid="export-currency"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 pt-2">
        <Button
          onClick={() => download("csv")}
          disabled={downloading !== null}
          variant="outline"
          className="gap-2"
          data-testid="export-csv-btn"
        >
          <FileText className="h-4 w-4" />
          {downloading === "csv"
            ? tx(lang, "Downloading…", "Descargando…", "Baixando…")
            : tx(lang, "Download CSV", "Descargar CSV", "Baixar CSV")}
        </Button>
        <Button
          onClick={() => download("xlsx")}
          disabled={downloading !== null}
          className="gap-2 bg-emerald-600 hover:bg-emerald-700"
          data-testid="export-xlsx-btn"
        >
          <FileSpreadsheet className="h-4 w-4" />
          {downloading === "xlsx"
            ? tx(lang, "Downloading…", "Descargando…", "Baixando…")
            : tx(lang, "Download Excel", "Descargar Excel", "Baixar Excel")}
        </Button>
      </div>
    </Card>
  );
}
